import type { CheckInView } from "./checkin.js";
import type { ServiceFacts, ServiceSignal } from "./services.js";
import type { TimelineItem } from "./views.js";
import type {
  AccountSettings,
  Action,
  ActionKind,
  ActionTarget,
  AlertSettings,
  AuditEntry,
  Channel,
  ChannelKind,
  CheckInEdit,
  Decision,
  Diagnosis,
  HeartbeatStatus,
  HostConnection,
  Level,
  PasskeyCredential,
  Person,
  PolicyMode,
  PolicyPreset,
  PolicyRule,
  Problem,
  ProblemState,
  QueueMessage,
  WeekStats,
} from "./wire.js";

/**
 * Service contracts. Workers talk through these over service bindings and Durable Object RPC.
 * Implementations live in cloud/*; tests use in-memory fakes of the same interfaces.
 */

/** Binding names, shared by wrangler configs and code. */
export const Bindings = {
  account: "ACCOUNT",
  ingestQueue: "INGEST_QUEUE",
  deliver: "DELIVER",
  escalate: "ESCALATE",
  fix: "FIX",
  digest: "DIGEST",
  channelCheck: "CHANNEL_CHECK",
  auditBucket: "AUDIT",
  /** KV: sha256(ingest key) → { accountId, projectId, revoked }. Written by api, read by ingest. */
  keys: "KEYS",
} as const;

/** ID prefixes. IDs are `${prefix}_${ulid}`. */
export const IdPrefix = {
  account: "acc",
  project: "prj",
  problem: "prb",
  action: "act",
  person: "per",
  channel: "chn",
  key: "key",
  connection: "con",
  question: "qst",
} as const;

export type ProposeActionInput = {
  problemId: string;
  kind: ActionKind;
  title: string;
  params: Record<string, unknown>;
  target: ActionTarget | null;
  proposedBy: "helper" | "agent" | "human";
  reversible: boolean;
  /** Audit actor, e.g. `agent:<tokenId>`. Defaults to `proposedBy` when absent. */
  actor?: string;
};

export type ApprovalChallenge = { actionId: string; challenge: string; expiresAt: number };

/** Proof sent by a phone or browser. For passkeys this is a WebAuthn assertion over the challenge. */
export type ApprovalProof =
  | {
      method: "passkey";
      credentialId: string;
      clientDataJSON: string;
      authenticatorData: string;
      signature: string;
    }
  | { method: "tap"; sessionId: string };

export type ProblemRate = {
  problemId: string;
  windowS: number;
  /** Events (sum of `count`) seen in the window. */
  count: number;
  perMinute: number;
  /** Epoch ms when the rate was measured. */
  at: number;
};

export type IngestResult = { opened: string[]; updated: string[]; ignored: number };

/** AccountDO: one per customer, the single writer for everything about that account. */
export interface AccountApi {
  ingest(messages: QueueMessage[]): Promise<IngestResult>;
  getProblem(id: string): Promise<Problem | null>;
  listProblems(q: { state?: ProblemState; limit?: number }): Promise<Problem[]>;
  setDiagnosis(problemId: string, diagnosis: Diagnosis): Promise<Problem>;
  ack(problemId: string, personId: string): Promise<Problem>;
  resolve(problemId: string, actor: string, summary: string): Promise<Problem>;
  /** Back to "open" (clears the ack) after a fix failed or was reverted, so escalation runs again. */
  reopen(problemId: string, actor: string, reason: string): Promise<Problem>;
  /** Events counted into this problem over the trailing `windowS` seconds. Used by Fix to verify. */
  getProblemRate(problemId: string, windowS: number): Promise<ProblemRate>;
  /** Counts for the weekly report, over [fromMs, toMs). */
  weekStats(fromMs: number, toMs: number): Promise<WeekStats>;

  proposeAction(input: ProposeActionInput): Promise<Action>;
  getAction(id: string): Promise<Action | null>;
  requestApproval(actionId: string, personId: string): Promise<ApprovalChallenge>;
  approve(actionId: string, personId: string, proof: ApprovalProof): Promise<Action>;
  reject(actionId: string, personId: string): Promise<Action>;
  setActionState(actionId: string, state: Action["state"], result?: Action["result"]): Promise<Action>;

  getRules(): Promise<PolicyRule[]>;
  setRule(rule: PolicyRule, personId: string): Promise<PolicyRule[]>;
  applyPreset(preset: PolicyPreset, personId: string): Promise<PolicyRule[]>;

  listPeople(): Promise<Person[]>;
  upsertPerson(p: Person): Promise<Person>;
  listChannels(): Promise<Channel[]>;
  upsertChannel(c: Channel): Promise<Channel>;

  audit(fromSeq?: number, limit?: number): Promise<AuditEntry[]>;
  verifyAudit(): Promise<{ ok: boolean; brokenAt: number | null }>;
}

/**
 * AccountDO passkey store, separate from AccountApi so existing fakes stay valid. cloud/api registers a
 * credential after the WebAuthn ceremony; AccountDO verifies approval assertions against it.
 */
export interface AccountPasskeysApi {
  addPasskey(c: PasskeyCredential): Promise<void>;
  listPasskeys(personId: string): Promise<Array<Omit<PasskeyCredential, "publicKey">>>;
  removePasskey(personId: string, credentialId: string): Promise<void>;
}

/** Secret reference format used in channel config and connections: `secret:<name>:<scope>`. */
export const SECRET_REF_RE = /^secret:([a-z0-9_]{1,64}):([A-Za-z0-9_-]{1,64})$/;

/**
 * Customer secrets added at runtime (their Twilio token, Slack token, host access tokens), stored
 * by AccountDO encrypted at rest (AES-256-GCM, per-account data key wrapped by SECRETS_MASTER_KEY).
 * `resolveSecret` is internal: deliver and flows call it over the ACCOUNT binding; api and MCP never
 * expose it. Audit entries record the reference, never the value.
 */
export interface AccountSecretsApi {
  /** Stores (or replaces) a secret and returns its reference `secret:<name>:<scope>`. */
  putSecret(name: string, scope: string, value: string, actor: string): Promise<string>;
  /** Returns the plaintext for a reference. Throws AccountError "not_found" when missing. */
  resolveSecret(ref: string): Promise<string>;
}

/**
 * Project → host connections, owned by AccountDO. A separate interface beside AccountApi so existing
 * fakes still compile. api writes them (POST /v1/connections, token via putSecret first); core uses
 * them to target helper actions; flows' Fix gets the token through `connectionAccessToken`.
 */
export interface AccountConnectionsApi {
  upsertConnection(c: HostConnection, actor: string): Promise<HostConnection>;
  listConnections(projectId?: string): Promise<HostConnection[]>;
  removeConnection(connectionId: string, actor: string): Promise<void>;
  /** Internal (Fix flow only, never exposed by api or MCP): the connection's token, via its secretRef. */
  connectionAccessToken(connectionId: string): Promise<string>;
}

/** One delivery attempt Escalate made, reported to AccountDO after each send. */
export type DeliveryRecord = {
  personId: string;
  channelKind: ChannelKind;
  ok: boolean;
  /** Epoch ms of the send. */
  at: number;
  /** Idempotency key (workflow instance + step + channel), so a retried report counts once. */
  key?: string;
};

/** The problem screen in one read: actions, plain-words timeline and when a person was first paged. */
export type ProblemDetail = {
  problem: Problem;
  actions: Action[];
  timeline: TimelineItem[];
  /** First successful delivery to a person; null when nobody was paged. */
  pagedAt: number | null;
};

/**
 * Per-problem reads and writes, beside AccountApi so existing fakes still compile. Reads use
 * indexed per-problem tables (actions by problem, a per-problem event table), never an audit scan.
 */
export interface AccountProblemsApi {
  /**
   * "I'll handle it": turns down every fix still waiting (proposed / awaiting approval) and marks the
   * problem as the person's, in one transaction. Idempotent. Returns the rejected actions.
   */
  handleProblem(problemId: string, personId: string): Promise<{ problem: Problem; actions: Action[] }>;
  /** Actions for one problem, oldest first. */
  listActions(problemId: string): Promise<Action[]>;
  /** Plain-words timeline, oldest first: the newest `limit` entries (default 100, max 500). */
  problemTimeline(problemId: string, limit?: number): Promise<TimelineItem[]>;
  /** Problem, actions, timeline and pagedAt in one call; null when the problem does not exist. */
  problemDetail(problemId: string, timelineLimit?: number): Promise<ProblemDetail | null>;
  /** Escalate reports each send; the first ok delivery sets the problem's pagedAt. */
  recordDelivery(problemId: string, d: DeliveryRecord): Promise<void>;
}

/** Account alert settings (quiet hours, wake-for list, morning summary). */
export interface AccountAlertsApi {
  getAlertSettings(): Promise<AlertSettings>;
  setAlertSettings(s: AlertSettings, personId: string): Promise<AlertSettings>;
}

/**
 * One escalation at a time per problem. Every Escalate run claims the problem before it pages and
 * keeps the claim alive (a lease) while it runs. A second run for the same problem stands down
 * (`go: false`) while the holder is alive, so nobody is paged twice. A "wake" run takes over from a
 * "tell" run (an upgrade must page now). An expired lease (holder died) can be taken: louder, never
 * quieter.
 */
export type EscalationClaim = {
  go: boolean;
  /** The run holding the problem after this call. */
  holder: string;
  /**
   * Additive. When a "wake" run takes over from a "tell" run (or one that just finished), who that
   * run already reached and how. Wake skips those channels and goes on to the call, so nobody gets
   * the same alert twice (the escalation is upgraded in place).
   */
  alreadyReached?: Array<{ personId: string; channelKind: ChannelKind }>;
};

export interface AccountEscalationApi {
  /** Claim or refresh. Idempotent for the holder (Workflow step retries). */
  claimEscalation(problemId: string, runId: string, level: "wake" | "tell", leaseMs: number): Promise<EscalationClaim>;
  /** Drop the claim if `runId` holds it. Idempotent. */
  releaseEscalation(problemId: string, runId: string): Promise<void>;
}

/** Account-wide settings: the time zone people see times in. */
export interface AccountSettingsApi {
  getSettings(): Promise<AccountSettings>;
  /** Owner only (the route checks). Throws AccountError invalid_input for an unknown zone. */
  setTimeZone(timeZone: string, personId: string): Promise<AccountSettings>;
}

/**
 * The app's display name per project ("Maya's Shop"), so alerts can say which app broke. The name is
 * owned by cloud/api (DirectoryDO); api copies it here on create and rename. Separate from AccountApi so
 * existing fakes stay valid. Unknown project: null, and alerts leave the name out.
 */
export interface AccountProjectNamesApi {
  setProjectName(projectId: string, name: string): Promise<void>;
  projectName(projectId: string): Promise<string | null>;
}

/**
 * Scheduled-job check-ins, beside AccountApi so existing fakes still compile. Runs arrive through
 * `ingest` (IngestEvent type "checkin"); the first one creates the check-in. api reads and edits them.
 */
export interface AccountCheckInsApi {
  /** Every check-in, or one project's, by project then name. */
  listCheckIns(projectId?: string): Promise<CheckInView[]>;
  /**
   * Creates or edits a check-in from the web app. From then on the SDK's schedule no longer changes it.
   * Throws AccountError invalid_input when the edit doesn't match CheckInEdit.
   */
  setCheckIn(projectId: string, slug: string, edit: CheckInEdit, personId: string): Promise<CheckInView>;
  /** Stops watching a job (and closes its open problem). Throws AccountError not_found. */
  removeCheckIn(projectId: string, slug: string, personId: string): Promise<void>;
}

/** App liveness from heartbeats. */
export interface AccountStatusApi {
  heartbeatStatus(): Promise<HeartbeatStatus[]>;
}

/**
 * Services and their activity, beside AccountApi so existing fakes still compile. Every ingested signal
 * (heartbeats aside) is kept in a bounded per-service log (see Activity), including ones that opened
 * or changed nothing, such as an alarm going OK with no open problem.
 */
export interface AccountServicesApi {
  /** What core has seen of each service of one app. */
  serviceFacts(projectId: string): Promise<ServiceFacts[]>;
  /** Newest first: signals under any of `services` (a unit may arrive under several names). */
  serviceActivity(projectId: string, services: string[], limit?: number): Promise<ServiceSignal[]>;
}

/**
 * Errors and traffic for one problem over the trailing `windowS` seconds. Fix's verify needs both:
 * zero errors with zero traffic is "nobody tried", not "fixed".
 */
export type ProblemTraffic = {
  problemId: string;
  windowS: number;
  /** Events counted into this problem in the window (same as getProblemRate's count). */
  errors: number;
  /**
   * Successful requests (SDK request metrics, status < 500) in the window, from the problem's
   * project. For a payments problem, only requests on payment routes count.
   */
  requests: number;
  /** Heartbeats from the problem's project in the window. */
  heartbeats: number;
  /** The project has sent request metrics before. When false, heartbeats are the only traffic signal. */
  requestMetrics: boolean;
  /** Epoch ms when measured. */
  at: number;
};

/** Beside AccountApi so existing fakes still compile. AccountDO implements it. */
export interface AccountTrafficApi {
  getProblemTraffic(problemId: string, windowS: number): Promise<ProblemTraffic>;
}

/**
 * Typed AccountDO errors. Errors lose their class across RPC, so the message starts with `[code]`;
 * use `accountErrorCode(err)` to read it back.
 */
export const AccountErrorCodes = [
  "not_found",
  "invalid_input",
  "invalid_state",
  "approval_missing",
  "approval_expired",
  "approval_replayed",
  "approval_mismatch",
  "passkey_required",
  "proof_invalid",
] as const;
export type AccountErrorCode = (typeof AccountErrorCodes)[number];

export class AccountError extends Error {
  constructor(
    readonly code: AccountErrorCode,
    detail: string,
  ) {
    super(`[${code}] ${detail}`);
    this.name = "AccountError";
  }
}

/** Reads the AccountErrorCode from an error thrown by AccountDO (works across RPC). */
export function accountErrorCode(err: unknown): AccountErrorCode | null {
  if (!(err instanceof Error)) return null;
  const code = /^\[([a-z_]+)\]/.exec(err.message)?.[1] ?? "";
  return (AccountErrorCodes as readonly string[]).includes(code) ? (code as AccountErrorCode) : null;
}

export type DeliveryRequest = {
  accountId: string;
  problemId: string;
  level: Level;
  person: Person;
  channel: Channel;
  /** Plain words. */
  title: string;
  body: string;
  /** Buttons offered on the device. */
  buttons: Array<"approve" | "ack" | "open" | "take_over">;
  actionId?: string;
  deepLink: string;
};
export type DeliveryResult = { ok: boolean; providerId?: string; error?: string; at: number };

/**
 * A channel test. `accountId` is required for any channel whose config references the account's
 * saved secrets (Twilio, Slack, Telegram, webhook); without it those secrets cannot resolve.
 */
export type ChannelTestRequest = { channel: Channel; person: Person; accountId?: string };

/** cloud/deliver: one adapter per channel kind. */
export interface DeliverApi {
  send(req: DeliveryRequest): Promise<DeliveryResult>;
  /**
   * Sends a clearly labelled test message. One object argument: `test` is workerd's reserved
   * test-handler name, and an RPC call to it only delivers the first argument.
   */
  testChannel(req: ChannelTestRequest): Promise<DeliveryResult>;
  /**
   * @deprecated Not usable over RPC (workerd treats `test` as its test handler and drops all but the
   * first argument); pl-deliver no longer exposes it. Use testChannel. Kept only so callers still
   * typecheck while flows and api switch; it will be removed.
   */
  test(channel: Channel, person: Person, accountId?: string): Promise<DeliveryResult>;
}

export type DecideInput = {
  problemKey: string;
  title: string;
  floor: Decision["floor"];
  /** Current vs baseline for the signal, when known. */
  rate: { now: number; baseline: number | null };
  recentDeploy: { release: string; at: number } | null;
  samples: Array<{ type: string; message: string; route?: string }>;
};

/** cloud/decide: floor → baseline → Jev → Claude → Haiku → rules. Never returns "log" for a floor. */
export interface Decider {
  decide(input: DecideInput): Promise<Decision>;
}

export type DiagnoseInput = DecideInput & {
  deploys: Array<{ release: string; at: number; diffSummary?: string }>;
  allowedKinds: ActionKind[];
};

export interface Helper {
  diagnose(input: DiagnoseInput): Promise<Diagnosis>;
}

/** Policy evaluation is pure and lives in packages/schema so every service agrees. */
export type PolicyVerdict = { mode: PolicyMode; rule: PolicyRule | null; reason: string };

export type ConnectorCapabilities = { undo: boolean; restart: boolean; scale: boolean; events: boolean };
/** Static AWS credentials (Pagerless's own IAM principal), used only to call STS AssumeRole. Never logged. */
export type AwsCredentials = { accessKeyId: string; secretAccessKey: string; sessionToken?: string };
/** Cross-account access the customer granted: their role ARN plus the ExternalId Pagerless issued for them. */
export type AwsRoleGrant = { roleArn: string; externalId: string; source: AwsCredentials };
export type ConnectorContext = {
  accessToken: string;
  fetch: typeof fetch;
  now: () => number;
  /** Self-hosted hosts (e.g. Coolify): the customer's API base URL, like `https://coolify.example.com/api/v1`. */
  baseUrl?: string;
  /** AWS hosts: the role to assume. `accessToken` is unused for AWS. */
  aws?: AwsRoleGrant;
};
export type Version = { id: string; createdAt: number; label: string; live: boolean };
export type ConnectorResult = { ok: boolean; detail: string };

/** cloud/connectors: one implementation per host. Unsupported operations throw NotSupported. */
export interface Connector {
  readonly hostId: string;
  readonly capabilities: ConnectorCapabilities;
  listVersions(ctx: ConnectorContext, resource: string): Promise<Version[]>;
  undo(ctx: ConnectorContext, resource: string, toVersionId: string): Promise<ConnectorResult>;
  restart(ctx: ConnectorContext, resource: string): Promise<ConnectorResult>;
  scale(ctx: ConnectorContext, resource: string, factor: number): Promise<ConnectorResult>;
}

export class NotSupported extends Error {
  constructor(hostId: string, op: string) {
    super(`${hostId} does not support ${op}`);
    this.name = "NotSupported";
  }
}

/** Workflow params. */
export type EscalateParams = {
  accountId: string;
  problemId: string;
  /**
   * Additive. "tell": send at tell level whatever the problem's level (quiet hours apply). Used by Fix
   * when it couldn't confirm a fix for lack of traffic: a human should know, but not be woken.
   */
  level?: "tell";
  /** Additive. Plain-words body to send instead of the problem's own copy (max 1000 chars). */
  note?: string;
};
/**
 * `mode` "verify" (additive, default "act"): the fix was already done elsewhere (e.g. the customer's
 * agent reported it via action_report, action state "verifying"); Fix only watches the error rate,
 * then marks done + resolves, or fails + escalates. Instance ID: WorkflowIds.verify(actionId).
 */
export type FixParams = { accountId: string; problemId: string; actionId: string; mode?: "act" | "verify" };
/** `weekEndMs` defaults to the time the workflow starts; the week is the 7 days before it. */
export type DigestParams = { accountId: string; weekEndMs?: number };
/**
 * Daily channel test (started by core's alarm, ID WorkflowIds.channelCheck(accountId, dayStartMs)):
 * DELIVER.test per channel, records lastTestAt/lastTestOk, reports broken ones on a working one.
 */
export type ChannelCheckParams = { accountId: string };

/**
 * Deterministic Workflow instance IDs, so a retried start is idempotent ("already exists" = started).
 * Escalate after a failed or reverted fix uses a distinct ID per action.
 */
export const WorkflowIds = {
  escalate: (problemId: string) => `esc_${problemId}`,
  escalateAfterFix: (problemId: string, actionId: string) => `esc_${problemId}_${actionId}`,
  fix: (actionId: string) => `fix_${actionId}`,
  verify: (actionId: string) => `vfy_${actionId}`,
  digest: (accountId: string, weekEndMs: number) => `dig_${accountId}_${weekEndMs}`,
  channelCheck: (accountId: string, dayMs: number) => `chk_${accountId}_${dayMs}`,
} as const;

/**
 * Canonical ingest address. SDKs default to `${baseUrl}${eventsPath}` with `Authorization: Bearer <key>`;
 * PAGERLESS_ENDPOINT overrides the full events URL. events.pagerless.dev is pl-prod-ingest's custom domain.
 */
export const Ingest = {
  baseUrl: "https://events.pagerless.dev",
  eventsPath: "/v1/events",
  heartbeatPath: "/v1/heartbeat",
  /** POST `${checkinsPath}/:slug` with an optional CheckInBody: a scheduled job reporting a run. */
  checkinsPath: "/v1/checkins",
  otlpLogsPath: "/v1/otlp/v1/logs",
  otlpTracesPath: "/v1/otlp/v1/traces",
  /** AWS SNS HTTPS subscription (CloudWatch alarms, EventBridge). Key via basic auth or ?key=. */
  awsSnsPath: "/v1/aws/sns",
  /** Alert webhooks from other tools: POST `${hooksPath}/<HookSource>/<key>` (see hooks.ts). */
  hooksPath: "/v1/hooks",
  /** OTLP/HTTP metrics (JSON or protobuf): delta HTTP server histograms become request counts. */
  otlpMetricsPath: "/v1/otlp/v1/metrics",
  /** Local dev: ingest runs inside the api dev session on :8787 under /ingest. */
  localEventsUrl: "http://localhost:8787/ingest/v1/events",
} as const;

/** Defaults; overridable by env. */
export const Timing = {
  /** Seconds between escalation steps (push → call → backup). 300 in prod, small in dev. */
  escalateStepS: 300,
  approvalTtlS: 600,
  verifyWindowS: 300,
  /** Polls of the problem's event rate during the verify window. */
  verifyPolls: 3,
  /**
   * With no errors but too little traffic to tell, verify keeps watching up to this many windows
   * before it reports "couldn't confirm".
   */
  verifyMaxWindows: 4,
  /** Successful requests since the fix needed to call it recovered (when the app sends request metrics). */
  verifyMinRequests: 5,
  /** Heartbeats since the fix needed when the app sends no request metrics. */
  verifyMinHeartbeats: 2,
  sampleTtlDays: 30,
  maxSamplesPerPattern: 5,
} as const;
